import { useMemo, useState } from 'react';
import { CircleCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styles from './ImplantVerification.module.css';
import SETTING_KEYS from '../../constants/setting-keys';
import LoadingRing from '../ui-components/LoadingRing';
import DatapipeLink from '../general/DatapipeLink';
import {
    getDatapipeImplantEditUrl,
    getDatapipeImplantViewUrl,
} from '../../utils/datapipeLinks';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import {
    Select,
    SelectContent,
    SelectItem as ShadcnSelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    Legend
} from 'recharts';

interface ImplantVerificationDisplayProps {
    implants: any[];
    loading: boolean;
    error: string | null;
}

const ImplantVerificationDisplay = ({
    implants,
    loading,
    error,
}: ImplantVerificationDisplayProps) => {
    const navigate = useNavigate();

    // State for chart visibility
    const [showChart, setShowChart] = useState(false);

    // State for sort mode
    const [sortMode, setSortMode] = useState<'urgency' | 'views' | 'expiry'>('urgency');

    const sortedImplants = useMemo(() => {
        const sorted = [...implants];
        if (sortMode === 'urgency') {
            sorted.sort((a, b) => (a.daysUntilNextReview ?? 0) - (b.daysUntilNextReview ?? 0));
        } else if (sortMode === 'views') {
            sorted.sort((a, b) => (b.totalViews ?? 0) - (a.totalViews ?? 0));
        } else {
            sorted.sort((a, b) => {
                const aExpiry = a[SETTING_KEYS.expiryDate]
                    ? new Date(a[SETTING_KEYS.expiryDate]).getTime()
                    : 0;
                const bExpiry = b[SETTING_KEYS.expiryDate]
                    ? new Date(b[SETTING_KEYS.expiryDate]).getTime()
                    : 0;
                return aExpiry - bExpiry;
            });
        }
        return sorted;
    }, [implants, sortMode]);

    // Get color based on months ago (0 = current month, 11 = 11 months ago)
    const getBarColor = (monthsAgo: number) => {
        if (monthsAgo <= 3) {
            // Green for 3 months or less
            return '#28a745';
        } else if (monthsAgo <= 6) {
            // Yellow to orange transition (months 4-6)
            const progress = (monthsAgo - 3) / 3; // 0 to 1
            const r = Math.round(255 * progress + 40 * (1 - progress)); // 40 to 255
            const g = Math.round(167 * (1 - progress) + 193 * progress); // 167 to 193
            const b = Math.round(69 * (1 - progress)); // 69 to 0
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            // Orange to red transition (months 7-11)
            const progress = (monthsAgo - 6) / 5; // 0 to 1
            const r = 255; // Stay at 255
            const g = Math.round(193 * (1 - progress)); // 193 to 0
            const b = 0; // Stay at 0
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    // Calculating days to expiry
    const getDaysToExpiry = (expiryDateString: string) => {
        const currentDate = new Date();
        const expiryDate = new Date(expiryDateString);
        return Math.ceil(
            (expiryDate.getTime() - currentDate.getTime()) / (1000 * 3600 * 24)
        );
    };

    // Process implant data for monthly chart
    const getMonthlyImplantData = () => {
        const now = new Date();
        const monthlyData = [];

        // Generate past 12 months
        for (let i = 11; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthKey = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
            });

            // Count implants updated in this month
            const count = implants.filter((implant: any) => {
                const updatedDate = new Date(implant.updatedAt);
                return (
                    updatedDate.getFullYear() === date.getFullYear() &&
                    updatedDate.getMonth() === date.getMonth()
                );
            }).length;

            // Count implants with Edit-Status === 3 updated in this month
            const editStatus3Count = implants.filter((implant: any) => {
                const updatedDate = new Date(implant.updatedAt);
                return (
                    updatedDate.getFullYear() === date.getFullYear() &&
                    updatedDate.getMonth() === date.getMonth() &&
                    implant['Edit-Status'] === 3
                );
            }).length;

            monthlyData.push({
                month: monthKey,
                count: count,
                editStatus3Count: editStatus3Count,
                monthsAgo: i,
            });
        }

        return monthlyData;
    };

    // Process implant data for verification status pie chart
    const getVerificationStatusData = () => {
        let safe = 0;
        let approaching = 0;
        let overdue = 0;

        implants.forEach((implant) => {
            if (implant.daysUntilNextReview < 0) {
                overdue++;
            } else if (implant.daysUntilNextReview < 30) {
                approaching++;
            } else {
                safe++;
            }
        });

        return [
            { name: 'Safe (>30 days)', value: safe, fill: '#28a745' },
            { name: 'Approaching (<30 days)', value: approaching, fill: '#fd7e14' },
            { name: 'Overdue', value: overdue, fill: '#dc3545' },
        ].filter(item => item.value > 0); // Only show segments with data
    };

    return (
        <div className={styles.wrapper}>
            {/* Chart Toggle Button */}
            {!loading && implants.length > 0 && (
                <div className="mb-4">
                    <Button
                        onClick={() => setShowChart(!showChart)}
                        variant="outline"
                    >
                        {showChart ? 'Hide Chart' : 'Show Chart'}
                    </Button>
                </div>
            )}

            {/* Charts Section */}
            {!loading && implants.length > 0 && showChart && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', marginBottom: '30px' }}>
                    {/* Verification Status Pie Chart */}
                    <div
                        style={{
                            padding: '20px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                        }}
                    >
                        <h3
                            style={{
                                marginBottom: '10px',
                                color: '#495057',
                                fontSize: '18px',
                                fontWeight: '600',
                            }}
                        >
                            Current Verification Status
                        </h3>
                        {/* @ts-ignore */}
                        <ResponsiveContainer width="100%" height={300}>
                            {/* @ts-ignore */}
                            <PieChart>
                                {/* @ts-ignore */}
                                <Pie
                                    data={getVerificationStatusData()}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={110}
                                    label
                                >
                                    {getVerificationStatusData().map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                                {/* @ts-ignore */}
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    }}
                                />
                                {/* @ts-ignore */}
                                <Legend verticalAlign="bottom" height={36}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Monthly Implant Updates Chart */}
                    <div
                        style={{
                            padding: '20px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                        }}
                    >
                        <h3
                            style={{
                                marginBottom: '10px',
                                color: '#495057',
                                fontSize: '18px',
                                fontWeight: '600',
                            }}
                        >
                            Implant Updates - Past 12 Months
                        </h3>
                        
                        <div
                            style={{
                                marginBottom: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                fontSize: '12px',
                                color: '#6c757d',
                            }}
                        >
                            {/* Bar Type Legend */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '20px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            background:
                                                'linear-gradient(to right, #28a745, #ffc107, #fd7e14, #dc3545)',
                                            borderRadius: '2px',
                                        }}
                                    />
                                    <span>
                                        Total Implants Updated (age-colored)
                                    </span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: '#6c757d',
                                            borderRadius: '2px',
                                        }}
                                    />
                                    <span>Verified Implants</span>
                                </div>
                            </div>

                            {/* Color Age Legend */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '20px',
                                }}
                            >
                                <span style={{ fontWeight: '500' }}>
                                    Age-based colors:
                                </span>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: '#28a745',
                                            borderRadius: '2px',
                                        }}
                                    />
                                    <span>Recent (0-3 months)</span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            background:
                                                'linear-gradient(to right, #ffc107, #fd7e14)',
                                            borderRadius: '2px',
                                        }}
                                    />
                                    <span>Moderate (4-6 months)</span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            background:
                                                'linear-gradient(to right, #fd7e14, #dc3545)',
                                            borderRadius: '2px',
                                        }}
                                    />
                                    <span>Older (7+ months)</span>
                                </div>
                            </div>
                        </div>

                        {/* @ts-ignore */}
                        <ResponsiveContainer width="100%" height={300}>
                            {/* @ts-ignore */}
                            <BarChart
                                data={getMonthlyImplantData()}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                {/* @ts-ignore */}
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e9ecef" 
                                />
                                {/* @ts-ignore */}
                                <XAxis 
                                    dataKey="month" 
                                    tick={{ fontSize: 12, fill: '#6c757d' }} 
                                    axisLine={{ stroke: '#dee2e6' }} 
                                />
                                {/* @ts-ignore */}
                                <YAxis 
                                    tick={{ fontSize: 12, fill: '#6c757d' }} 
                                    axisLine={{ stroke: '#dee2e6' }} 
                                />
                                {/* @ts-ignore */}
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    }}
                                    formatter={(value, name) => {
                                        if (name === 'count') return [value, 'Total Implants Updated'];
                                        if (name === 'editStatus3Count') return [value, 'Verified Implants'];
                                        return [value, name];
                                    }}
                                    labelStyle={{ color: '#495057' }}
                                />
                                {/* @ts-ignore */}
                                <Bar 
                                    dataKey="count" 
                                    radius={[4, 4, 0, 0]} 
                                    name="count"
                                >
                                    {getMonthlyImplantData().map((entry, index) => (
                                        // @ts-ignore
                                        <Cell 
                                            key={`cell-${index}`} 
                                            fill={getBarColor(entry.monthsAgo)} 
                                        />
                                    ))}
                                </Bar>
                                {/* @ts-ignore */}
                                <Bar 
                                    dataKey="editStatus3Count" 
                                    fill="#6c757d" 
                                    radius={[4, 4, 0, 0]} 
                                    name="editStatus3Count" 
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* <h2>Total number of implants to be verified {implants.length}</h2> */}
            {error && <p className="text-destructive mb-4">{error}</p>}
            {loading ? (
                <div className="flex justify-center py-16">
                    <LoadingRing size="large" />
                </div>
            ) : sortedImplants.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                    <h2 className="text-lg font-semibold mb-1">
                        No implants found
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-md">
                        There are no implants in this category right now, or you have already verified them all. Great job!
                    </p>
                </div>
            ) : (
                <>
                    <div className="mb-5 flex items-center gap-4">
                        <Select value={sortMode} onValueChange={(val: any) => setSortMode(val)}>
                            <SelectTrigger className="w-[200px]">
                                <SelectValue placeholder="Sort by" />
                            </SelectTrigger>
                            <SelectContent>
                                <ShadcnSelectItem value="urgency">Sort by: Most Urgent</ShadcnSelectItem>
                                <ShadcnSelectItem value="views">Sort by: Most Views</ShadcnSelectItem>
                                <ShadcnSelectItem value="expiry">Sort by: Oldest Expiry</ShadcnSelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="rounded-lg border bg-card">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[35%]">Implant Info</TableHead>
                                    <TableHead className="w-[10%] text-center">Views</TableHead>
                                    <TableHead className="w-[10%] text-center">Frequency</TableHead>
                                    <TableHead className="w-[15%] text-center">Expected Review</TableHead>
                                    <TableHead className="w-[15%] text-center">Last Updated</TableHead>
                                    <TableHead className="w-[10%] text-center">Verification</TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {sortedImplants.map((implant: any) => {
                                    return (
                                        <TableRow key={implant._id}>
                                            <TableCell
                                                onClick={() => {
                                                    navigate(
                                                        `/implant/${implant._id}`
                                                    );
                                                }}
                                                className="cursor-pointer font-medium"
                                            >
                                                <b>{implant.Manufacturer}</b>
                                                <br></br>
                                                {implant.Model}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <b>{implant.totalViews ?? 0} views</b>
                                                <br />
                                                <span
                                                    style={{
                                                        fontSize: '11px',
                                                        color: '#6c757d',
                                                    }}
                                                >
                                                    {implant.implantViews ?? 0} views
                                                    {' · '}
                                                    {implant.ifuOpens ?? 0} IFU
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <span style={{ fontWeight: 500 }}>Every {implant.reviewFrequencyDays} days</span>
                                                    <span style={{ fontSize: '11px', color: '#6c757d' }}>
                                                        Score: {implant.urgencyScore ?? 0}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge
                                                    variant={
                                                        implant.daysUntilNextReview < 0 ? 'destructive' :
                                                            implant.daysUntilNextReview < 30 ? 'outline' : 'secondary'
                                                    }
                                                    className={implant.daysUntilNextReview >= 0 && implant.daysUntilNextReview < 30 ? "border-orange-500 text-orange-600" : ""}
                                                >
                                                    {implant.daysUntilNextReview < 0
                                                        ? `${Math.abs(implant.daysUntilNextReview)} days overdue`
                                                        : `In ${implant.daysUntilNextReview} days`}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-center text-sm text-muted-foreground">
                                                {new Date(
                                                    implant.updatedAt
                                                ).toLocaleDateString()}
                                                <br></br>
                                                <span
                                                    className={
                                                        !implant[
                                                            SETTING_KEYS
                                                                .expiryDate
                                                        ]
                                                            ? ''
                                                            : getDaysToExpiry(
                                                                implant[
                                                                SETTING_KEYS
                                                                    .expiryDate
                                                                ]
                                                            ) <= 0
                                                                ? styles.expired
                                                                : getDaysToExpiry(
                                                                    implant[
                                                                    SETTING_KEYS
                                                                        .expiryDate
                                                                    ]
                                                                ) < 8
                                                                    ? styles.soonToExpire
                                                                    : ''
                                                    }
                                                >
                                                    {!implant[
                                                        SETTING_KEYS.expiryDate
                                                    ]
                                                        ? ''
                                                        : getDaysToExpiry(
                                                            implant[
                                                            SETTING_KEYS
                                                                .expiryDate
                                                            ]
                                                        ) <= 0
                                                            ? 'Expired'
                                                            : `${getDaysToExpiry(
                                                                implant[
                                                                SETTING_KEYS
                                                                    .expiryDate
                                                                ]
                                                            )} days`}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <div className="flex flex-col items-center gap-1.5">
                                                    <DatapipeLink
                                                        href={getDatapipeImplantViewUrl(
                                                            implant._id
                                                        )}
                                                        label="Verify in datapipe"
                                                        icon={CircleCheck}
                                                        variant="default"
                                                    />
                                                    <DatapipeLink
                                                        href={getDatapipeImplantEditUrl(
                                                            implant._id
                                                        )}
                                                        label="Edit in datapipe"
                                                    />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ImplantVerificationDisplay;
